import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { Download, Users, Wallet, TrendingUp, Calendar, ChevronDown, ChevronRight } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { exportSourceBasedReportToExcel, getSourceBasedReportSummary, exportSourceDetailsToExcel } from "../../components/Api/GmsSourceBasedReport";
import CustomerProfileModal from "../../components/CustomerProfileModal";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

// Format dates properly to avoid timezone issues
const formatDateForAPI = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

// Format date for display
const formatDate = (dateString) => {
  try {
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return "N/A";
  }
};

// Get first and last day of current month
const getCurrentMonthRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { firstDay, lastDay };
};

export default function SourceBasedReportPage() {
  const [loading, setLoading] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [sourcePage, setSourcePage] = useState(1);
  const [pageSize] = useState(10);
  const [expandedSources, setExpandedSources] = useState(new Set());
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [exportingSourceId, setExportingSourceId] = useState(null);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSourceBasedReport');

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  // Initialize dates to current month
  useEffect(() => {
    console.log("🔍 [SourceBasedReport] Component mounted/updated");
    console.log("🔍 [SourceBasedReport] Has permission:", hasPermission);
    console.log("🔍 [SourceBasedReport] Is admin:", isAdmin);

    if (hasPermission) {
      const { firstDay, lastDay } = getCurrentMonthRange();
      console.log("🔍 [SourceBasedReport] Setting default dates:", { firstDay, lastDay });
      setFromDate(firstDay);
      setToDate(lastDay);
    } else {
      console.log("⚠️ [SourceBasedReport] No permission, redirecting to Dashboard");
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isAdmin]);

  // Load summary data when dates change
  useEffect(() => {
    if (hasPermission && fromDate && toDate) {
      loadSummaryData();
    }
  }, [fromDate, toDate, hasPermission]);

  const loadSummaryData = async () => {
    if (!fromDate || !toDate) return;

    try {
      setLoadingSummary(true);
      const filterData = {
        fromDate: formatDateForAPI(fromDate),
        toDate: formatDateForAPI(toDate),
      };

      console.log("📊 [SourceBasedReport] Loading summary data with filter:", filterData);
      const summary = await getSourceBasedReportSummary(filterData);
      console.log("✅ [SourceBasedReport] Summary data loaded:", summary);
      setSummaryData(summary);
      setSourcePage(1);
      setExpandedSources(new Set());
    } catch (error) {
      console.error("❌ [SourceBasedReport] Error loading summary:", error);
      Swal.fire("Error", "Failed to load report summary. Please try again.", "error");
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleExport = async () => {
    console.log("🖱️ [SourceBasedReport] Export button clicked");
    console.log("🖱️ [SourceBasedReport] Current state:", { fromDate, toDate, loading });

    if (!fromDate || !toDate) {
      console.log("❌ [SourceBasedReport] Validation failed: Missing dates", { fromDate, toDate });
      Swal.fire("Error", "Please select both From Date and To Date", "error");
      return;
    }

    if (fromDate > toDate) {
      console.log("❌ [SourceBasedReport] Validation failed: From date > To date", { fromDate, toDate });
      Swal.fire("Error", "From Date cannot be greater than To Date", "error");
      return;
    }

    try {
      console.log("✅ [SourceBasedReport] Validation passed, starting export...");
      setLoading(true);
      console.log("🔄 [SourceBasedReport] Loading state set to true");

      const filterData = {
        fromDate: formatDateForAPI(fromDate),
        toDate: formatDateForAPI(toDate),
      };

      console.log("📤 [SourceBasedReport] Calling API with filter data:", filterData);
      console.log("📤 [SourceBasedReport] Formatted dates:", {
        originalFromDate: fromDate,
        originalToDate: toDate,
        formattedFromDate: filterData.fromDate,
        formattedToDate: filterData.toDate
      });

      console.log("📤 [SourceBasedReport] About to call exportSourceBasedReportToExcel...");
      await exportSourceBasedReportToExcel(filterData);
      console.log("✅ [SourceBasedReport] API call completed successfully");

      Swal.fire("Success", "Report exported successfully", "success");
    } catch (error) {
      console.error("❌ [SourceBasedReport] Error in handleExport:", error);
      console.error("❌ [SourceBasedReport] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        stack: error.stack
      });
      Swal.fire("Error", "Failed to export report. Please try again.", "error");
    } finally {
      console.log("🔄 [SourceBasedReport] Setting loading state to false");
      setLoading(false);
    }
  };

  const handleSourceExport = async (sourceId, sourceName, e) => {
    e.stopPropagation(); // Prevent row expansion when clicking export button

    if (!fromDate || !toDate) {
      Swal.fire("Error", "Please select both From Date and To Date", "error");
      return;
    }

    if (fromDate > toDate) {
      Swal.fire("Error", "From Date cannot be greater than To Date", "error");
      return;
    }

    try {
      setExportingSourceId(sourceId);
      const filterData = {
        fromDate: formatDateForAPI(fromDate),
        toDate: formatDateForAPI(toDate),
      };

      await exportSourceDetailsToExcel(sourceId, sourceName, filterData);
      Swal.fire("Success", "Source details exported successfully", "success");
    } catch (error) {
      console.error("❌ [SourceBasedReport] Error in handleSourceExport:", error);
      Swal.fire("Error", "Failed to export source details. Please try again.", "error");
    } finally {
      setExportingSourceId(null);
    }
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Source Based Customer Count and Revenue Report" breadcrumbItem="Source Based Report" />

        <Card>
          <CardBody>
            <h4 className="card-title mb-4">Source Based Customer Count and Revenue Report</h4>

            <Form>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>From Date</Label>
                    <DatePicker
                      selected={fromDate}
                      onChange={(date) => setFromDate(date)}
                      dateFormat="dd/MM/yyyy"
                      className="form-control"
                      maxDate={toDate || new Date()}
                      placeholderText="Select From Date"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>To Date</Label>
                    <DatePicker
                      selected={toDate}
                      onChange={(date) => setToDate(date)}
                      dateFormat="dd/MM/yyyy"
                      className="form-control"
                      minDate={fromDate}
                      maxDate={new Date()}
                      placeholderText="Select To Date"
                    />
                  </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <div className="d-flex gap-2 w-100">
                    <Button
                      color="info"
                      onClick={loadSummaryData}
                      disabled={loadingSummary || !fromDate || !toDate}
                      className="flex-fill"
                    >
                      {loadingSummary ? (
                        <>
                          <ClipLoader size={16} color="#fff" className="me-2" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <TrendingUp size={18} className="me-2" />
                          Load Report
                        </>
                      )}
                    </Button>
                    <Button
                      color="success"
                      onClick={handleExport}
                      disabled={loading || !fromDate || !toDate}
                      className="flex-fill"
                    >
                      {loading ? (
                        <>
                          <ClipLoader size={16} color="#fff" className="me-2" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download size={18} className="me-2" />
                          Export to Excel
                        </>
                      )}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        {/* Summary Cards */}
        {summaryData && (
          <>
            <Row className="mt-3">
              <Col md={3}>
                <Card className="border-primary">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="avatar-sm rounded-circle bg-primary bg-soft text-center">
                          <Calendar className="text-primary" size={24} style={{ marginTop: '12px' }} />
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <p className="text-muted mb-1">Total Booking</p>
                        <h4 className="mb-0">{summaryData.totalBookings || 0}</h4>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-success">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="avatar-sm rounded-circle bg-success bg-soft text-center">
                          <Users className="text-success" size={24} style={{ marginTop: '12px' }} />
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <p className="text-muted mb-1">Booking Invoiced</p>
                        <h4 className="mb-0">{summaryData.bookingInvoiced || 0}</h4>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-info">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="avatar-sm rounded-circle bg-info bg-soft text-center">
                          <Wallet className="text-info" size={24} style={{ marginTop: '12px' }} />
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <p className="text-muted mb-1">Invoiced Revenue</p>
                        <h4 className="mb-0">{formatCurrency(summaryData.invoicedRevenue || 0)}</h4>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-warning">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="avatar-sm rounded-circle bg-warning bg-soft text-center">
                          <TrendingUp className="text-warning" size={24} style={{ marginTop: '12px' }} />
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <p className="text-muted mb-1">Active Sources</p>
                        <h4 className="mb-0">{summaryData.activeSourcesCount || 0}</h4>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3} className="mt-md-0 mt-3">
                <Card className="border-danger">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="avatar-sm rounded-circle bg-danger bg-soft text-center">
                          <Wallet className="text-danger" size={24} style={{ marginTop: '12px' }} />
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <p className="text-muted mb-1">Direct Sales Revenue</p>
                        <h4 className="mb-0" style={{ color: '#f46a6a' }}>{formatCurrency(summaryData.directRevenue || 0)}</h4>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Source Summary Table */}
            {summaryData.sourceSummaries && summaryData.sourceSummaries.length > 0 && (
              <Card className="mt-3">
                <CardBody>
                  <h5 className="card-title mb-3">Source Performance Summary</h5>
                  <div className="table-responsive">
                    <Table bordered hover>
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '50px' }}></th>
                          <th>#</th>
                          <th>Source Name</th>
                          <th className="text-end">Bookings</th>
                          <th className="text-end">Invoiced</th>
                          <th className="text-end">Invoiced Revenue</th>
                          <th className="text-end">Direct Sales Revenue</th>
                          <th style={{ width: '120px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.sourceSummaries
                          .slice((sourcePage - 1) * pageSize, sourcePage * pageSize)
                          .map((source, index) => {
                            const isExpanded = expandedSources.has(source.source_Id);
                            const customers = summaryData.customerSummaries?.filter(c => c.source_Id === source.source_Id) || [];

                            return (
                              <React.Fragment key={source.source_Id}>
                                <tr
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    const newExpanded = new Set(expandedSources);
                                    if (isExpanded) {
                                      newExpanded.delete(source.source_Id);
                                    } else {
                                      newExpanded.add(source.source_Id);
                                    }
                                    setExpandedSources(newExpanded);
                                  }}
                                >
                                  <td className="text-center">
                                    {isExpanded ? (
                                      <ChevronDown size={18} />
                                    ) : (
                                      <ChevronRight size={18} />
                                    )}
                                  </td>
                                  <td>{(sourcePage - 1) * pageSize + index + 1}</td>
                                  <td><strong>{source.sourceName}</strong></td>
                                  <td className="text-end">{source.totalBookings || 0}</td>
                                  <td className="text-end">{source.invoicedBookings || 0}</td>
                                  <td className="text-end">{formatCurrency(source.invoicedRevenue || 0)}</td>
                                  <td className="text-end text-danger">{formatCurrency(source.directRevenue || 0)}</td>
                                  <td>
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={(e) => handleSourceExport(source.source_Id, source.sourceName, e)}
                                      disabled={exportingSourceId === source.source_Id || !fromDate || !toDate}
                                      style={{ minWidth: '100px' }}
                                    >
                                      {exportingSourceId === source.source_Id ? (
                                        <>
                                          <ClipLoader size={12} color="#fff" className="me-1" />
                                          Exporting...
                                        </>
                                      ) : (
                                        <>
                                          <Download size={14} className="me-1" />
                                          Export
                                        </>
                                      )}
                                    </Button>
                                  </td>
                                </tr>
                                {isExpanded && customers.length > 0 && (
                                  <tr>
                                    <td colSpan="7" style={{ padding: 0, backgroundColor: '#f8f9fa' }}>
                                      <div style={{ padding: '15px' }}>
                                        <h6 className="mb-3">Customers for {source.sourceName}</h6>
                                        <Table bordered size="sm">
                                          <thead className="table-light">
                                            <tr>
                                              <th>Customer Name</th>
                                              <th className="text-end">Total Bookings</th>
                                              <th className="text-end">Invoiced Bookings</th>
                                              <th className="text-end">Direct Sales Revenue</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {customers.map((customer) => (
                                              <tr key={customer.customer_Id}>
                                                <td>
                                                  <a
                                                    href="#"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      setSelectedCustomerId(customer.customer_Id);
                                                      setShowCustomerProfile(true);
                                                    }}
                                                    style={{ color: '#556ee6', textDecoration: 'none' }}
                                                  >
                                                    {customer.customerName}
                                                  </a>
                                                </td>
                                                <td className="text-end">{customer.totalBookings || 0}</td>
                                                <td className="text-end">{customer.invoicedBookings || 0}</td>
                                                <td className="text-end text-danger">{formatCurrency(customer.directRevenue || 0)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </Table>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </tbody>
                    </Table>
                  </div>
                  {summaryData.sourceSummaries.length > pageSize && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>
                          {summaryData.sourceSummaries.length > 0 ? (sourcePage - 1) * pageSize + 1 : 0}
                        </span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>
                          {Math.min(sourcePage * pageSize, summaryData.sourceSummaries.length)}
                        </span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>
                          {summaryData.sourceSummaries.length}
                        </span> entries
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => setSourcePage(Math.max(1, sourcePage - 1))}
                          disabled={sourcePage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => setSourcePage(Math.min(Math.ceil(summaryData.sourceSummaries.length / pageSize), sourcePage + 1))}
                          disabled={sourcePage >= Math.ceil(summaryData.sourceSummaries.length / pageSize)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {(!summaryData.sourceSummaries || summaryData.sourceSummaries.length === 0) && (
              <Card className="mt-3">
                <CardBody>
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">No data available for the selected date range.</p>
                  </div>
                </CardBody>
              </Card>
            )}
          </>
        )}

        {!summaryData && !loadingSummary && (
          <Card className="mt-3">
            <CardBody>
              <div className="text-center py-4">
                <p className="text-muted mb-0">Select date range and click "Load Report" to view summary data.</p>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Customer Profile Modal */}
        <CustomerProfileModal
          isOpen={showCustomerProfile}
          toggle={() => {
            setShowCustomerProfile(false);
            setSelectedCustomerId(null);
          }}
          customerId={selectedCustomerId}
        />
      </Container>
    </div>
  );
}

